import React, { useState, useEffect, useMemo } from "react";
import Datagrid from "@ui/components/DataGrid";
import { HEADINGS } from "../../modules/Users/constants";
import { modifyUsers } from "../../modules/Users/helpers";
import { ListHeader } from "../../../ui/structures/ListHeader";
import { ListButton } from "../../../ui/components/ListButton";
import { Modal } from "../../../ui/components/Modal";
import { UsersList } from "./UsersList";
import { useFetch } from "../../../hooks/fetch.hook";
import { getUsers } from "../../modules/Users/_api";
import {
  getOrganisationUsers,
  unlinkUsers,
} from "../../modules/Organisations/_api";
import { renderUsersRow } from "./helpers";
import { Loader } from "../../../ui/components/Loader";

export const OrganisationUsers = ({ organisationId, admins_tenant }) => {
  const { request } = useFetch();

  const [usersList, setUsersList] = useState([]);
  const [linkedUsers, setLinkedUsers] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState({});
  const [actionLoading, setActionLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleModalOpen = () => setModalOpen(true);
  const handleClose = () => {
    setSelected({});
    setModalOpen(false);
  };

  useEffect(() => {
    setLoading(true);
    request(getOrganisationUsers, organisationId)
      .then((users) => {
        if (!users) return;
        setLinkedUsers(modifyUsers(users));
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (admins_tenant) return;
    request(getUsers).then((data) => data && setUsersList(modifyUsers(data)));
    // eslint-disable-next-line
  }, []);

  const getNonLinkedUsers = () => {
    const arrayOfIds = linkedUsers.map((item) => item.id);
    return usersList.filter((item) => !arrayOfIds.includes(item.id));
  };

  const renderButtons = () => {
    return admins_tenant ? null : (
      <>
        <div className="mr-5">
          <ListButton label="Add" onClick={handleModalOpen} data-testid="add" />
        </div>
        <ListButton
          label="Remove Users"
          onClick={removeUser}
          disabled={unlinkDisabled}
          data-testid="remove"
        />
      </>
    );
  };

  const updateUsersList = () => {
    setActionLoading(true);
    return request(getOrganisationUsers, organisationId)
      .then((data) => data && setLinkedUsers(modifyUsers(data)))
      .finally(() => {
        setActionLoading(false);
      });
  };

  const removeUser = () => {
    setActionLoading(true);
    const payload = Object.keys(selected)
      .filter((item) => Boolean(selected[item]))
      .map((item) => ({
        id: item,
      }));
    request(unlinkUsers, payload, organisationId).then((data) => {
      if (!data) return;
      updateUsersList();
      setSelected({});
    });
  };

  const unlinkDisabled = useMemo(
    () => Object.values(selected).filter(Boolean).length < 1,
    [selected]
  );

  return (
    <>
      {actionLoading && <Loader title="Loading..." isOpen={actionLoading} />}
      {modalOpen && (
        <Modal
          isOpen={modalOpen}
          submitable
          onClose={handleClose}
          modalContent={
            <UsersList
              handleClose={handleClose}
              data={getNonLinkedUsers()}
              organisationId={organisationId}
              setUsersList={setUsersList}
              updateUsersList={updateUsersList}
              setActionLoading={setActionLoading}
            />
          }
        />
      )}
      <div className="row justify-content-center">
        <div className="col-12">
          <div className="bg-white rounded py-7 px-10">
            <ListHeader title="Users" renderButtons={renderButtons} />
            <Datagrid
              data={linkedUsers}
              headings={HEADINGS}
              renderRow={renderUsersRow}
              selected={selected}
              setSelected={setSelected}
              selectable
              loading={loading}
            />
          </div>
        </div>
      </div>
    </>
  );
};
